import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { FirmService } from '../../../../Services/firm.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { NotificationService } from '../../../../Services/notification.service';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';

export interface Transaction {
  srNo: number;
  date: string;
  account: string;
  details: string;
  firm: string;
  openingBalance: number;
  debit: number;
  credit: number;
  closingBalance: number;
}

@Component({
  selector: 'app-account-ledger',
  standalone: true,
  imports: [NgFor, NgIf, DecimalPipe, RouterLink, FormsModule],
  templateUrl: './account-ledger.component.html',
  styleUrl: './account-ledger.component.css'
})
export class AccountLedgerComponent implements OnInit, OnDestroy {

  accountName: string = '';
  primaryAccount: string = '';
  accountId: number | null = null;

  firms: any[] = [];
  selectedFirmId: any = '';
  fromDate: string = '';
  toDate: string = '';
  selectedFirmName: string = '';
  isMetalAccount: boolean = false;


  // Summary fields
  openingBalance: number = 0;
  openingBalanceCrDr: string = 'DR';
  currentTotalDebit: number = 0;
  currentTotalCredit: number = 0;
  closingBalance: number = 0;
  closingBalanceCrDr: string = 'DR';

  transactions: any[] = [];
  paginatedTransactions: any[] = [];

  // Pagination & Search
  p: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;
  searchTerm: string = '';

  private firmSubscription: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private firmService: FirmService,
    private firmSelectionService: FirmSelectionService,
    private accountService: AccountService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {

    // 1. Get account ID from query params and fetch details
    this.route.queryParams.subscribe(params => {
      const id = params['id'] ? Number(params['id']) : null;
      if (id && id !== this.accountId) {
        this.accountId = id;
        this.getAccountDetails(id);
      }
    });

    // 2. Fetch all firms for dropdown and filter
    this.getFirms();

    // 3. Subscribe to global firm selection for default and restriction
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.selectedFirmId = firm.id;
        // Re-filter firms just in case
        this.getFirms();
        this.onFilterChange();
      }
    });

    // Initial check from service
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.selectedFirmId = currentFirm.id;
      this.setFirmName();
      this.onFilterChange();
    }
  }

  setFirmName() {
    if (this.selectedFirmId && this.firms.length > 0) {
      const firm = this.firms.find(f => f.id == this.selectedFirmId);
      this.selectedFirmName = firm ? firm.name : '';
    }
  }


  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  getFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        const allFirms = res.data || [];
        const currentFirm = this.firmSelectionService.getselectedFirmName();

        if (currentFirm) {
          // Filter to show only the logged-in/selected firm
          this.firms = allFirms.filter((f: any) => f.id == currentFirm.id);
          if (this.firms.length > 0) {
            this.selectedFirmId = this.firms[0].id;
          }
        } else {
          this.firms = allFirms;
        }

        if (this.selectedFirmId) {
          this.setFirmName();
        }
      }
    });
  }



  getAccountDetails(id: number) {
    this.accountService.getAccount(id).subscribe({
      next: (res: any) => {
        const acc = res.data;
        this.accountName = acc.acc_user_acc || acc.acc_name || 'N/A';
        this.primaryAccount = acc.acc_main_acc || 'N/A';
        this.isMetalAccount = this.accountName.toLowerCase().includes('metal');

        // Set Opening Balance from Account
        this.openingBalance = parseFloat(acc.acc_cash_balance) || 0;
        this.openingBalanceCrDr = acc.acc_cash_balance_crdr || 'DR';

        this.onFilterChange();
      },
      error: (err) => {
        console.error('Error fetching account details', err);
        this.accountName = 'Error';
        this.primaryAccount = 'Error';
      }
    });
  }

  onFilterChange() {
    if (!this.accountId) return;

    const params: any = {
      account: this.accountId,
    };

    if (this.selectedFirmId) {
      params.firm = this.selectedFirmId;
      this.setFirmName();
    }
    if (this.fromDate) params.from_date = this.fromDate;
    if (this.toDate) params.to_date = this.toDate;


    this.accountService.getJournalTransactions(params).subscribe({
      next: (res: any) => {
        this.processTransactions(res.data || []);
      },
      error: (err) => {
        console.error('Error fetching transactions', err);
      }
    });
  }

  private processTransactions(data: any[]) {
    let runningBalance = this.openingBalance;
    let runningCrDr = this.openingBalanceCrDr;

    this.currentTotalDebit = 0;
    this.currentTotalCredit = 0;

    this.transactions = data.map((t: any, index: number) => {
      const isDebit = t.jrtr_dr_acc_id == this.accountId;
      const debitAmt = isDebit ? (parseFloat(t.jrtr_dr_amt) || 0) : 0;
      const creditAmt = !isDebit ? (parseFloat(t.jrtr_cr_amt) || 0) : 0;

      const grossWeight = isDebit ? (parseFloat(t.jrtr_ggw_dr) || 0) : (parseFloat(t.jrtr_ggw_cr) || 0);
      const purity = isDebit ? (parseFloat(t.jrtr_gfw_dr) || 0) : (parseFloat(t.jrtr_gfw_cr) || 0);

      this.currentTotalDebit += debitAmt;
      this.currentTotalCredit += creditAmt;

      const openingBalForThisRow = runningBalance;
      const openingCrDrForThisRow = runningCrDr;

      // Update Running Balance
      // (DR + DR = increase, DR + CR = decrease, etc.)
      let netChange = 0;
      if (isDebit) {
        // It's a DEBIT to this account
        netChange = debitAmt;
      } else {
        // It's a CREDIT to this account
        netChange = -creditAmt;
      }

      // If existing balance is CR, treat it as negative for DR-based calculation
      let currentBal = (runningCrDr === 'DR') ? runningBalance : -runningBalance;
      currentBal += netChange;

      runningBalance = Math.abs(currentBal);
      runningCrDr = currentBal >= 0 ? 'DR' : 'CR';

      // Show the current account name instead of contra-account (per user request)
      const currentAccountName = isDebit ?
        (t.debit_account?.acc_user_acc || t.debit_account?.acc_name || 'N/A') :
        (t.credit_account?.acc_user_acc || t.credit_account?.acc_name || 'N/A');

      return {
        srNo: index + 1,
        journalId: t.jrtr_jrnl_id,
        date: t.jrtr_date ? new Date(t.jrtr_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A',
        account: currentAccountName,
        details: t.jrtr_desc || t.journal?.jrnl_desc || 'N/A',
        firm: t.firm?.name || 'N/A',
        openingBalance: openingBalForThisRow,
        openingBalanceCrDr: openingCrDrForThisRow,
        debit: debitAmt,
        credit: creditAmt,
        closingBalance: runningBalance,
        closingBalanceCrDr: runningCrDr,
        grossWeight: grossWeight,
        purity: purity
      };
    });

    this.closingBalance = runningBalance;
    this.closingBalanceCrDr = runningCrDr;

    this.p = 1;
    this.filterTable();
  }

  filterTable() {
    let filtered = this.transactions;
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = this.transactions.filter(t =>
        t.details.toLowerCase().includes(search) ||
        t.account.toLowerCase().includes(search)
      );
    }

    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
    if (this.p > this.totalPages && this.totalPages > 0) {
      this.p = this.totalPages;
    }

    const start = (this.p - 1) * this.itemsPerPage;
    this.paginatedTransactions = filtered.slice(start, start + this.itemsPerPage);
  }

  onPageChange(page: number) {
    this.p = page;
    this.filterTable();
  }

  onSearchChange() {
    this.p = 1;
    this.filterTable();
  }

  get pageNumbers(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.p;
    const maxPagesToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        startPage = 2;
        endPage = 3;
      }
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  }

  goToPreviousPage() {
    if (this.p > 1) {
      this.onPageChange(this.p - 1);
    }
  }

  goToNextPage() {
    if (this.p < this.totalPages) {
      this.onPageChange(this.p + 1);
    }
  }

  printLedger() {
    const data = this.transactions;
    const fromDateStr = this.fromDate ? new Date(this.fromDate).toLocaleDateString('en-GB') : 'N/A';
    const toDateStr = this.toDate ? new Date(this.toDate).toLocaleDateString('en-GB') : 'N/A';
    const generatedOn = new Date().toLocaleString('en-GB');

    let printContent = `
    <html>
    <head>
      <title>Account Ledger - ${this.accountName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; color: #333; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #C19725; padding-bottom: 10px; }
        .header h2 { margin: 0; color: #C19725; text-transform: uppercase; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
        .info-item { font-weight: bold; }
        .info-item span { font-weight: normal; }
        
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; border-top: 2px solid #C19725; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        
        .footer-summary { margin-left: auto; width: 300px; border: 1px solid #ddd; padding: 10px; background-color: #fff9f9; }
        .footer-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dashed #ddd; }
        .footer-row:last-child { border-bottom: none; font-weight: bold; font-size: 14px; }

        @media print {
          @page { size: auto; margin: 10mm; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h2>Account Ledger Report</h2>
        <p>Firm: ${this.selectedFirmName || 'N/A'}</p>
      </div>

      <div class="info-grid">
        <div class="info-item">Account: <span>${this.accountName}</span></div>
        <div class="info-item">Type: <span>${this.primaryAccount}</span></div>
        <div class="info-item">From Date: <span>${fromDateStr}</span></div>
        <div class="info-item">To Date: <span>${toDateStr}</span></div>
        <div class="info-item">Generated On: <span>${generatedOn}</span></div>
      </div>

      <table>
        <thead>
          <tr>
            <th class="text-center">Sr. No.</th>
            <th>Date</th>
            <th>Account</th>
            <th>Details</th>
            <th class="text-right">Opening Balance</th>
            <th class="text-right">Debit</th>
            <th class="text-right">Credit</th>
            <th class="text-right">Closing Balance</th>
          </tr>
        </thead>
        <tbody>
        
    `;

    data.forEach((t) => {
      let debitHtml = '-';
      if (t.debit > 0) {
        if (this.isMetalAccount) {
          debitHtml = `${t.debit.toFixed(3)} gm Net`;
          if (t.grossWeight > 0) {
            debitHtml += `<br><span style="font-size:10px;color:#555;">Gs: ${t.grossWeight.toFixed(3)} gm (${t.purity.toFixed(2)}%)</span>`;
          }
        } else {
          debitHtml = `₹ ${t.debit.toFixed(2)}`;
        }
      }

      let creditHtml = '-';
      if (t.credit > 0) {
        if (this.isMetalAccount) {
          creditHtml = `${t.credit.toFixed(3)} gm Net`;
          if (t.grossWeight > 0) {
            creditHtml += `<br><span style="font-size:10px;color:#555;">Gs: ${t.grossWeight.toFixed(3)} gm (${t.purity.toFixed(2)}%)</span>`;
          }
        } else {
          creditHtml = `₹ ${t.credit.toFixed(2)}`;
        }
      }

      printContent += `
        <tr>
          <td class="text-center">${t.srNo}</td>
          <td>${t.date}</td>
          <td>${t.account}</td>
          <td>${t.details}</td>
          <td class="text-right">${this.isMetalAccount ? '' : '₹ '}${t.openingBalance.toFixed(this.isMetalAccount ? 3 : 2)} ${t.openingBalanceCrDr}${this.isMetalAccount ? ' gm' : ''}</td>
          <td class="text-right">${debitHtml}</td>
          <td class="text-right">${creditHtml}</td>
          <td class="text-right">${this.isMetalAccount ? '' : '₹ '}${t.closingBalance.toFixed(this.isMetalAccount ? 3 : 2)} ${t.closingBalanceCrDr}${this.isMetalAccount ? ' gm' : ''}</td>
        </tr>
      `;
    });


    printContent += `
        </tbody>
      </table>

      <div class="footer-summary">
        <div class="footer-row">
          <span>Opening Balance:</span>
          <span>${this.isMetalAccount ? '' : '₹ '}${this.openingBalance.toFixed(this.isMetalAccount ? 3 : 2)} ${this.openingBalanceCrDr}${this.isMetalAccount ? ' gm' : ''}</span>
        </div>
        <div class="footer-row">
          <span>Current Totals:</span>
          <span>${this.isMetalAccount ? '' : '₹ '}${this.currentTotalDebit.toFixed(this.isMetalAccount ? 3 : 2)} DR / ${this.isMetalAccount ? '' : '₹ '}${this.currentTotalCredit.toFixed(this.isMetalAccount ? 3 : 2)} CR${this.isMetalAccount ? ' gm' : ''}</span>
        </div>
        <div class="footer-row">
          <span>Closing Balance:</span>
          <span>${this.isMetalAccount ? '' : '₹ '}${this.closingBalance.toFixed(this.isMetalAccount ? 3 : 2)} ${this.closingBalanceCrDr}${this.isMetalAccount ? ' gm' : ''}</span>
        </div>
      </div>

      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() { window.close(); };
        }
      </script>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  }

  navigateToUpdate() {

    if (this.accountId) {
      this.router.navigate(['/accounts/add-new-account'], { queryParams: { id: this.accountId } });
    }
  }

  deleteRow(index: number) {
    const row = this.paginatedTransactions[index];
    if (!row?.journalId) {
      this.notificationService.showError('Journal reference not found for this row.', 'Delete');
      return;
    }

    if (!confirm('Delete this entry? It will be removed from all related accounts and daybook.')) {
      return;
    }

    this.accountService.deleteJournal(row.journalId).subscribe({
      next: () => {
        this.notificationService.showSuccess('Entry deleted successfully.', 'Success');
        this.onFilterChange();
      },
      error: (err) => {
        this.notificationService.showError(err.error?.message || 'Failed to delete entry.', 'Error');
      }
    });
  }

}
